import { getKitSciVersion } from '@repo/utils';
import { useSuspenseQueries } from '@tanstack/react-query';
import c from 'classnames';
import {
  ContainerSizes,
  FeedbackWidget,
  PageContainer,
  PageHeader,
  Typography,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import DisruptionModelSection from 'components/DisruptionModelSection/DisruptionModelSection';
import ImprovingGutResilienceCard from 'components/ImprovingGutResilienceCard/ImprovingGutResilienceCard';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import WhatCausesDisruptionsCard from 'components/WhatCausesDisruptionsCard/WhatCausesDisruptionsCard';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { evaluationToLevel } from 'helpers/gutResilience';
import { getActionsForSubMetric } from 'helpers/submetrics/getActionsForSubMetric';
import { getResilienceResultConfig } from 'helpers/submetrics/getResilienceResultConfig';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getActionPlanItemsQuery } from 'services/actionPlan/actionPlanData';
import { getSubMetricDetailQuery } from 'services/insights/useSubMetricDetail';
import { getTaxaOverTimeQuery } from 'services/insights/useTaxaOverTime';
import { FeedbackPageType } from 'types/Feedback';
import AboutSection from '../SubMetricData/AboutSection';
import ActionsReminderCard from '../SubMetricData/ActionsReminderCard';
import RelatedMetrics from '../SubMetricData/RelatedMetrics';
import OverTimeCard from '../SubMetricGraphs/cards/OverTimeCard';
import ResultCard from '../SubMetricGraphs/cards/ResultCard';
import { RelatedActions } from './RelatedActions';
import styles from './SubMetricDetail.module.css';

export const RESILIENCE_CATEGORY_KEY = 'Gut resilience score';

export const GutResilienceScoreDetail = () => {
  const { kitId, subMetricId } = useParams<{
    kitId: string;
    subMetricId: string;
  }>();
  const { state } = useLocation();
  const { currentKit } = useCurrentKitAndTinyAccount();
  const sciVersion = getKitSciVersion(currentKit);
  const [{ data: subMetric }, { data: allActions }] = useSuspenseQueries({
    queries: [
      getSubMetricDetailQuery(kitId, subMetricId, sciVersion),
      getActionPlanItemsQuery(kitId),
      getTaxaOverTimeQuery(kitId, parseInt(subMetricId ?? '')),
    ],
  });
  const startAPRef = useRef<HTMLElement>(null);
  const desktopAPRef = useRef<HTMLElement>(null);
  const actionItems = getActionsForSubMetric(allActions, subMetricId);
  const { trackEventOnce } = useAnalytics();

  const resilienceMetricId = subMetric?.gut_vaginal_health?.id;
  if (resilienceMetricId != null) {
    trackEventOnce('GRS Detail Page Viewed', String(resilienceMetricId), {
      score_value: subMetric.user_value,
      score_state: evaluationToLevel(subMetric.evaluation_color),
    });
  }

  useEffect(() => {
    if (state?.scrollTo !== 'actions') return;
    const target =
      startAPRef.current?.offsetParent != null
        ? startAPRef.current
        : desktopAPRef.current;
    if (target) {
      const scrollContainer = document.querySelector('#appWrapper');
      scrollContainer?.scrollTo({
        top: target.offsetTop - 10,
        behavior: 'smooth',
      });
    }
  }, [state]);

  const resilience = getResilienceResultConfig(subMetric);

  return (
    <PageContainer size={ContainerSizes.LG} className='pt-3'>
      <Breadcrumb
        className={styles.breadcrumb}
        overrides={{ [subMetricId ?? '']: subMetric?.name }}
      />
      <PageHeader
        title={RESILIENCE_CATEGORY_KEY}
        beta={subMetric?.gut_vaginal_health?.beta ?? true}
      />
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <div className={c('flex flex-column', styles.wideColumn)}>
            <ActionsReminderCard
              relatedActionsQty={actionItems.length}
              scrollTargetRef={startAPRef}
            />
            <ResultCard
              metric={subMetric}
              headline={
                resilience ? (
                  <Typography variant='display-s' color={resilience.color}>
                    {resilience.label}
                  </Typography>
                ) : undefined
              }
              subtitle={resilience?.scoreText}
            />
            <DisruptionModelSection
              userEvaluationColor={subMetric?.evaluation_color}
            />
            <section id='actions' ref={startAPRef} className='hide-on-desktop'>
              <RelatedActions actions={actionItems} />
            </section>
            <WhatCausesDisruptionsCard />
            <ImprovingGutResilienceCard />
            <AboutSection metric={subMetric} />
            <RelatedMetrics metric={subMetric} />
            {subMetric?.data_type !== 'string' &&
              !!subMetric?.graph_display && <OverTimeCard metric={subMetric} />}
            {!!kitId && !!subMetric?.gut_vaginal_health?.id && (
              <div className='mt-4 hide-on-desktop'>
                <FeedbackWidget
                  kitId={kitId}
                  pageType={FeedbackPageType.INSIGHT}
                  pageId={subMetric.gut_vaginal_health.id}
                />
              </div>
            )}
          </div>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <section ref={desktopAPRef} className='hide-on-mobile'>
            <RelatedActions actions={actionItems} />
          </section>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
};
